/**
 * Projects Page – Light workspace theme
 * Lists all projects as rectangular clickable cards with mock data
 * NotebookLM-inspired card style
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "@/hooks/use-translations";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { Plus, MoreVertical, X, Loader2, Pencil, Trash2, FolderKanban } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import { api, type Project } from "@/lib/api";
import { toast } from "sonner";
import { CreateProjectModal } from "@/components/create-project-modal";

export function ProjectsPage() {
	const t = useTranslations("projects");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const { activeTeam } = useActiveTeam();
	const [showAddModal, setShowAddModal] = useState(false);
	const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
	const [editingProject, setEditingProject] = useState<Project | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);

	const { data: projects = [], isLoading } = useQuery<Project[]>({
		queryKey: ["projects", user?.id, activeTeam?.id],
		queryFn: () =>
			api.getProjects({
				userId: user!.id,
				...(activeTeam ? { teamId: activeTeam.id } : {}),
			}),
		enabled: !!user?.id,
	});

	const handleProjectCreated = (chatId: string) => {
		queryClient.invalidateQueries({ queryKey: ["projects"] });
		navigate(`/workspace/${chatId}`);
	};

	const handleDeleteProject = async (projectId: string) => {
		try {
			await api.deleteProject(projectId);
			toast.success(t("deleted"));
			queryClient.invalidateQueries({ queryKey: ["projects"] });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("deleteFailed");
			toast.error(message);
		}
	};

	return (
		<div className="p-6 max-w-6xl mx-auto space-y-6">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2 }}
			>
				<h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
					<FolderKanban size={24} className="shrink-0 text-text-primary" aria-hidden="true" />
					{t("title")}
				</h1>
				<p className="mt-1 text-sm text-text-tertiary">{t("subtitle")}</p>
			</motion.div>

			{/* Projects Grid */}
			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
				{/* Create New Project Card */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
				>
					<div
						className="rounded-2xl border-2 border-dashed border-border-control hover:border-brand-700 bg-transparent hover:bg-brand-50 cursor-pointer transition-all group min-h-[200px] flex flex-col items-center justify-center gap-3 px-4"
						onClick={() => setShowAddModal(true)}
					>
						<div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center">
							<Plus
								size={22}
								className="text-text-primary"
							/>
						</div>
						<span className="text-sm text-text-tertiary group-hover:text-text-primary transition-colors">
							{t("addProject")}
						</span>
					</div>
				</motion.div>

								{/* Project Cards */}
								{isLoading ? (
									<div className="col-span-full flex items-center justify-center py-12">
										<Loader2 size={24} className="animate-spin text-text-tertiary" />
									</div>
								) : (
									projects.map((project, index) => (
										<motion.div
											key={project.id}
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ delay: (index + 1) * 0.05 }}
										>
											<div
												className="rounded-2xl border border-border-subtle bg-bg-surface cursor-pointer hover:shadow-lg transition-all group min-h-[200px] flex flex-col hover:border-brand-700 hover:bg-brand-50"
												onClick={() => navigate(`/workspace/${project.id}`)}
											>
												<div className="p-4 flex flex-col flex-1">
													{/* Top: Icon + Menu */}
													<div className="flex items-start justify-between">
														<div className="p-3 rounded-lg bg-brand-100">
															<FolderKanban size={32} className="shrink-0 text-text-primary" aria-hidden="true" />
														</div>
														<div className="relative">
															<button
																type="button"
																className="p-1 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-sunken opacity-0 group-hover:opacity-100 transition-all"
																onClick={(e) => {
																	e.stopPropagation();
																	setActiveDropdownId(activeDropdownId === project.id ? null : project.id);
																}}
																>
																	<MoreVertical size={16} />
																</button>

															{/* Dropdown Menu */}
															<AnimatePresence>
																{activeDropdownId === project.id && (
																	<>
																		<div
																			className="fixed inset-0 z-40"
																			onClick={() => setActiveDropdownId(null)}
																		/>
																		<motion.div
																			initial={{ opacity: 0, y: -10 }}
																			animate={{ opacity: 1, y: 0 }}
																			exit={{ opacity: 0, y: -10 }}
																			className="absolute right-0 top-full mt-2 w-48 rounded-xl bg-bg-surface shadow-popover border border-border-subtle z-50 overflow-hidden p-1"
																		>
																		<button
																			onClick={(e) => {
																				e.stopPropagation();
																				setActiveDropdownId(null);
																				setEditingProject(project);
																				setEditTitle(project.title);
																			}}
																			className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-bg-canvas transition-colors"
																		>
																			<Pencil size={14} className="text-text-secondary" />
																			<span className="text-sm text-text-primary">
																				{t("editTitle")}
																			</span>
																		</button>
																		<button
																			onClick={(e) => {
																				e.stopPropagation();
																				setActiveDropdownId(null);
																				handleDeleteProject(project.id);
																			}}
																			className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-bg-canvas transition-colors"
																		>
																			<Trash2 size={14} className="text-danger" />
																			<span className="text-sm text-danger">
																				{t("delete")}
																			</span>
																		</button>
																		</motion.div>
																	</>
																)}
															</AnimatePresence>
														</div>
													</div>

													{/* Bottom: Name + Date */}
													<div className="mt-auto pt-4">
														<h3 className="font-medium text-text-primary text-sm leading-snug line-clamp-2">
															{project.title}
														</h3>
														<p className="text-xs text-text-tertiary mt-1.5">
															{new Date(project.createdAt).toLocaleDateString("en-US", {
																day: "numeric",
																month: "short",
																year: "numeric",
															})}
														</p>
													</div>
												</div>
											</div>
										</motion.div>
									))
								)}
			</div>

			<CreateProjectModal
				open={showAddModal}
				onClose={() => setShowAddModal(false)}
				onCreated={handleProjectCreated}
			/>

			{/* Edit Title Modal */}
			<AnimatePresence>
				{editingProject && (
					<>
						<motion.div
							className="fixed inset-0 bg-scrim/30 z-50"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={() => {
								setEditingProject(null);
								setEditTitle("");
							}}
						/>
						<motion.div
							className="fixed inset-0 flex items-center justify-center z-50 p-4"
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
						>
							<Card
								className="w-full max-w-md shadow-popover"
								onClick={(e) => e.stopPropagation()}
							>
								<CardContent className="p-6">
									<div className="flex items-center justify-between mb-4">
										<h2 className="font-semibold text-text-primary">
											{t("editTitle")}
										</h2>
										<Button
											type="button"
											variant="ghost"
											size="icon-sm"
											onClick={() => {
												setEditingProject(null);
												setEditTitle("");
											}}
										>
											<X size={18} />
										</Button>
									</div>
									<form
										onSubmit={async (e) => {
											e.preventDefault();
											if (!editTitle.trim() || !editingProject) return;

											setIsUpdating(true);
											try {
												await api.updateProjectTitle(editingProject.id, {
													title: editTitle.trim(),
												});
												queryClient.invalidateQueries({ queryKey: ["projects"] });
												setEditingProject(null);
												setEditTitle("");
												toast.success(t("titleUpdated"));
											} catch (error) {
												const message =
													error instanceof Error
														? error.message
														: t("titleUpdateFailed");
												toast.error(message);
											} finally {
												setIsUpdating(false);
											}
										}}
									>
										<Input
											value={editTitle}
											onChange={(e) => setEditTitle(e.target.value)}
											placeholder={t("projectNamePlaceholder")}
											className="mb-4"
											autoFocus
											disabled={isUpdating}
										/>
										<div className="flex gap-3 pt-2">
											<Button
												type="button"
												variant="secondary"
												onClick={() => {
													setEditingProject(null);
													setEditTitle("");
												}}
												disabled={isUpdating}
												className="flex-1"
											>
												{t("cancel")}
											</Button>
											<Button
												type="submit"
												disabled={isUpdating || !editTitle.trim()}
												className="flex-1"
											>
												{isUpdating && (
													<Loader2 size={16} className="animate-spin" />
												)}
												{t("save")}
											</Button>
										</div>
									</form>
								</CardContent>
							</Card>
						</motion.div>
					</>
				)}
			</AnimatePresence>
		</div>
	);
}
